import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { eventApi } from '@/services/api';
import { useUser } from '@/contexts/UserContext';
import ZoomMeeting from '@/components/ZoomMeeting';
import './OnlineCoachingDetailsPage.css';

// Cache buster that changes on each page load but stays consistent during the session
// This ensures images load with fresh CORS headers on each page load
let pageLoadCacheBuster: string | null = null;

// Helper function to ensure image URLs load with proper CORS
// Adds a cache-busting parameter if the URL is from S3 to avoid stale cached responses without CORS headers
const ensureImageUrl = (url: string | undefined | null): string => {
  if (!url || url === '/placeholder.svg') {
    return '/placeholder.svg';
  }
  
  // If it's an S3 URL, add a cache-busting parameter to ensure fresh CORS headers
  // This helps avoid cached responses that don't include CORS headers
  if (url.includes('s3.ap-southeast-1.amazonaws.com') || url.includes('s3.amazonaws.com')) {
    // Remove any existing cache-busting parameters first
    const urlWithoutParams = url.split('?')[0];
    const separator = '?';
    // Initialize cache buster once per page load
    if (!pageLoadCacheBuster) {
      pageLoadCacheBuster = Date.now().toString();
    }
    // Use 'v' parameter for better cache-busting compatibility
    return `${urlWithoutParams}${separator}v=${pageLoadCacheBuster}`;
  }
  
  return url;
};

// Helper function to determine if a session is ongoing
// A session is ongoing if current time is between schedule_at and schedule_at + duration
const isSessionOngoing = (scheduleAt: string | undefined, duration: number | undefined): boolean => {
  if (!scheduleAt) return false;
  
  const now = new Date();
  const startTime = new Date(scheduleAt);
  const endTime = duration 
    ? new Date(startTime.getTime() + duration * 60 * 1000) // duration is in minutes, convert to milliseconds
    : new Date(startTime.getTime() + 60 * 60 * 1000); // Default to 1 hour if duration not provided
  
  return now >= startTime && now <= endTime;
};

// Helper function to format duration from minutes to "Xhrs Ymins" or "Xhrs" format
const formatDuration = (minutes: number | undefined): string => {
  if (!minutes) return '1hr';
  
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hours === 0) {
    return `${mins}mins`;
  } else if (mins === 0) {
    return `${hours}hr${hours > 1 ? 's' : ''}`;
  } else {
    return `${hours}hr ${mins}mins`;
  }
};

// API Response Types
interface ApiEventSchedule {
  id: number;
  fk_event_id: number;
  schedule_at: string;
  description?: string | null;
  learning_objectives?: string[] | null;
  requirements?: string[] | null;
  zoom_duration?: number;
  zoom_meeting_id?: string | null;
  zoom_meeting_password?: string | null;
  thumbnail_url?: string | null;
  banner_url?: string | null;
  host_profile_picture_url?: string | null;
  host_name?: string | null;
  host_experience?: string | null;
  max_seat?: number;
  registrations?: Array<{
    id: number;
    status_code: string;
    user?: {
      id: number;
      first_name: string;
      last_name?: string;
    };
  }>;
}

interface ApiEvent {
  id: number;
  title: string;
  description: string;
  type: string;
  status_code: string;
  schedules?: ApiEventSchedule[];
  creator?: {
    id: number;
    first_name: string;
    last_name?: string;
    email?: string;
  };
}

interface ApiEventsResponse {
  status: string;
  message: string;
  data: {
    data: ApiEvent[];
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
  };
}

const OnlineCoachingDetailsPage = () => {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const [session, setSession] = useState<{
    id: string;
    title: string;
    category: string;
    description: string;
    learn: string[];
    requirements: string[];
    zoomMeetingId?: string;
    zoomPassword?: string;
    scheduleAt?: string;
    zoomDuration?: number;
    eventType?: string;
    hostName?: string;
    hostExperience?: string;
    hostAvatar?: string;
  } | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'instructor'>('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [relatedSessions, setRelatedSessions] = useState<Array<{
    id: number;
    scheduleId: number;
    title: string;
    category: string;
    thumbnailUrl?: string;
    scheduleAt?: string;
    zoomDuration?: number;
    maxSeat?: number;
    registrations?: number;
  }>>([]);

  // Reset cache buster on component mount to ensure fresh CORS headers on each page load
  useEffect(() => {
    pageLoadCacheBuster = Date.now().toString();
  }, []);

  useEffect(() => {
    const fetchSchedule = async () => {
      if (!meetingId) {
        setError('Meeting ID is required');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Fetch events with schedules
        const response = await eventApi.getEvents({
          type: 'online talks',
          status: 'active',
          per_page: 100, // Get enough events to find the schedule
        });

        const data = response.data as ApiEventsResponse;

        console.log('API Response received:', data);
        console.log('Looking for meetingId:', meetingId);

        if (data.status === 'success' && data.data?.data) {
          // Find the schedule with matching ID
          let foundSchedule: ApiEventSchedule | null = null;
          let foundEvent: ApiEvent | null = null;

          for (const event of data.data.data) {
            if (event.schedules && event.schedules.length > 0) {
              console.log(`Checking event ${event.id} with ${event.schedules.length} schedules`);
              const schedule = event.schedules.find(
                (s) => s.id.toString() === meetingId
              );
              if (schedule) {
                console.log('Found matching schedule!', schedule);
                foundSchedule = schedule;
                foundEvent = event;
                break;
              } else {
                console.log(`Schedule IDs in event ${event.id}:`, event.schedules.map(s => s.id.toString()));
              }
            }
          }

          if (foundSchedule && foundEvent) {
            // Determine category from event type
            const categoryMap: Record<string, string> = {
              'online talks': 'Career Development',
              'prof networking': 'Professional Networking',
            };
            const category = categoryMap[foundEvent.type] || foundEvent.type;

            // Debug logging
            console.log('Found Schedule:', foundSchedule);
            console.log('Schedule Description:', foundSchedule.description);
            console.log('Learning Objectives:', foundSchedule.learning_objectives);
            console.log('Requirements:', foundSchedule.requirements);

            const sessionData = {
              id: foundSchedule.id.toString(),
              title: foundEvent.title,
              category,
              description: foundSchedule.description || foundEvent.description || '',
              learn: Array.isArray(foundSchedule.learning_objectives) 
                ? foundSchedule.learning_objectives 
                : [],
              requirements: Array.isArray(foundSchedule.requirements) 
                ? foundSchedule.requirements 
                : [],
              zoomMeetingId: foundSchedule.zoom_meeting_id || undefined,
              zoomPassword: foundSchedule.zoom_meeting_password || undefined,
              scheduleAt: foundSchedule.schedule_at || undefined,
              zoomDuration: foundSchedule.zoom_duration || undefined,
              eventType: foundEvent.type,
              hostName: foundSchedule.host_name || undefined,
              hostExperience: foundSchedule.host_experience || undefined,
              hostAvatar: ensureImageUrl(foundSchedule.host_profile_picture_url),
            };

            console.log('Setting session data:', sessionData);
            setSession(sessionData);

            // Fetch related sessions (other online talks events)
            const relatedEvents = data.data.data
              .flatMap((event) => {
                // Map each event's schedules to individual session items
                if (!event.schedules || event.schedules.length === 0) return [];
                return event.schedules
                  .filter((schedule) => {
                    // Exclude the current schedule
                    return schedule.id.toString() !== meetingId;
                  })
                  .map((schedule) => ({
                    id: event.id,
                    scheduleId: schedule.id,
                    title: event.title,
                    category: categoryMap[event.type] || event.type,
                    thumbnailUrl: schedule.thumbnail_url || schedule.banner_url || undefined,
                    scheduleAt: schedule.schedule_at || undefined,
                    zoomDuration: schedule.zoom_duration || undefined,
                    maxSeat: schedule.max_seat || undefined,
                    registrations: schedule.registrations?.length || 0,
                  }));
              })
              .slice(0, 3); // Get only 3 related sessions

            setRelatedSessions(relatedEvents);
          } else {
            console.error('Schedule not found for meetingId:', meetingId);
            setError('Schedule not found');
          }
        } else {
          setError('Failed to load schedule');
        }
      } catch (err: any) {
        console.error('Error fetching schedule:', err);
        setError(err.response?.data?.message || 'Failed to load schedule. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSchedule();
  }, [meetingId]);

  if (isLoading) {
    return (
      <div className="coaching-details-page">
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Loading session details...</p>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="coaching-details-page">
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p style={{ color: 'red' }}>{error || 'Session not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="coaching-details-page">
      {/* Back to Sessions button */}
      <div style={{ marginBottom: '1.5rem' }}>
        <button
          onClick={() => navigate('/online-coaching')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'transparent',
            border: 'none',
            color: '#9ca3af',
            cursor: 'pointer',
            fontSize: '0.875rem',
            padding: '0.5rem 0',
            transition: 'color 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#ffffff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#9ca3af';
          }}
        >
          <svg 
            width="16" 
            height="16" 
            viewBox="0 0 16 16" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            style={{ transform: 'rotate(180deg)' }}
          >
            <path 
              d="M6 12L10 8L6 4" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
          <span>Back to Sessions</span>
        </button>
      </div>

      {/* Top: Zoom Meeting area */}
      <div className="zoom-hero">
        <div className="zoom-container">
          <ZoomMeeting
            meetingNumber={session.zoomMeetingId || session.id}
            password={session.zoomPassword || '123456'}
            userName={user?.name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Guest User'}
            sessionTitle={session.title}
            scheduleAt={session.scheduleAt}
            zoomDuration={session.zoomDuration}
            eventType={session.eventType}
            scheduleId={meetingId}
          />
        </div>
      </div>

      {/* Title and quick actions */}
      <div className="details-header">
        <h1 className="title">{session.title}</h1>
        <div className="chips">
          <span className="chip purple">{session.category}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab ${activeTab === 'instructor' ? 'active' : ''}`}
          onClick={() => setActiveTab('instructor')}
        >
          Instructor
        </button>
      </div>

      {/* Overview Tab Content */}
      {activeTab === 'overview' && (
        <section className="about">
          <h2>About this Session</h2>
          <p className="summary">{session.description}</p>

          <div className="cols">
            <div className="col">
              <h3>What you'll learn</h3>
              {session.learn.length > 0 ? (
                <ul>
                  {session.learn.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p>No learning objectives specified.</p>
              )}
            </div>
            <div className="col">
              <h3>Requirements</h3>
              {session.requirements.length > 0 ? (
                <ul>
                  {session.requirements.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p>No requirements specified.</p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Instructor Tab Content */}
      {activeTab === 'instructor' && (
        <section className="about">
          <h2>Instructor</h2>
          {session.hostName ? (
            <div className="instructor-card" style={{ 
              display: 'flex', 
              gap: '1.5rem', 
              alignItems: 'flex-start',
              padding: '1.5rem',
              borderRadius: '8px',
              marginTop: '1rem'
            }}>
              <img 
                src={session.hostAvatar || '/placeholder.svg'} 
                alt={session.hostName}
                crossOrigin="anonymous"
                referrerPolicy="no-referrer"
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  objectFit: 'cover'
                }}
              />
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem', fontWeight: '600' }}>
                  {session.hostName}
                </h3>
                {session.hostExperience && (
                  <p style={{ margin: 0, color: '#ffffff', textAlign: 'justify', fontSize: '1.1rem', fontWeight: '400' }}>
                    {session.hostExperience}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <p>Instructor information not available.</p>
          )}
        </section>
      )}

      {/* Related Sessions */}
      <section className="related">
        <div className="related-head">
          <h2>Related Sessions</h2>
          <button className="view-all" onClick={() => navigate('/online-coaching')}>
            View All
          </button>
        </div>
        <div className="related-list">
          {relatedSessions.length > 0 ? (
            relatedSessions.map((relatedSession) => {
              // Format duration
              const durationText = formatDuration(relatedSession.zoomDuration);

              // Determine if session is ongoing or upcoming
              const isOngoing = isSessionOngoing(relatedSession.scheduleAt, relatedSession.zoomDuration);
              
              // Navigate to join (details page) if ongoing, or register page if upcoming
              const handleClick = () => {
                if (isOngoing) {
                  navigate(`/online-coaching/join/${relatedSession.scheduleId}`);
                } else {
                  navigate(`/online-coaching/register/${relatedSession.scheduleId}`);
                }
              };

              return (
                <div
                  className="related-item"
                  key={`${relatedSession.id}-${relatedSession.scheduleId}`}
                  onClick={handleClick}
                  style={{ cursor: 'pointer' }}
                >
                  <div
                    className="thumb"
                    style={{
                      backgroundImage: relatedSession.thumbnailUrl
                        ? `url(${ensureImageUrl(relatedSession.thumbnailUrl)})`
                        : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  />
                  <div className="meta">
                    <div className="category-tag">{relatedSession.category}</div>
                    <div className="name">{relatedSession.title}</div>
                    <div className="row">
                      {/* {relatedSession.registrations && relatedSession.registrations > 0 && (
                        <div className="meta-item">
                          <svg 
                            width="16" 
                            height="16" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2"
                          >
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                          <span>{relatedSession.registrations}</span>
                        </div>
                      )} */}
                      {relatedSession.zoomDuration && (
                        <div className="meta-item">
                          <svg 
                            width="16" 
                            height="16" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                          </svg>
                          <span>{durationText}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <p style={{ color: '#9ca3af', textAlign: 'center', padding: '1rem' }}>
              No related sessions available
            </p>
          )}
        </div>
      </section>
    </div>
  );
};

export default OnlineCoachingDetailsPage;


